import { Permissions } from '../../../../models/enums/permissions';
import cloneDeep from 'lodash/cloneDeep';
import * as React from 'react';
import { useDispatch } from "react-redux";
import { DataGroupHeader } from '../../../../components/data-group/DataGroupHeader';
import ResultDialog from '../../../../components/dialog-types/ResultDialog';
import { PageActions, ReadView } from '../../../../components/form-elements/_index';
import { PageBody } from '../../../../components/page-components/PageBody';
import { PageContent } from '../../../../components/page-components/PageContent';
import { PageHeading } from '../../../../components/page-components/PageHeading';

import { StatusCode } from '../../../../utils/api';
import { PageNames, Routes } from '../../../../utils/app-constants';
import { AppUtils } from '../../../../utils/app-utils';
import { toSlashedShortDate } from '../../../../utils/date-utils';
import { history, navigateToPage } from '../../../../utils/history';
import { ActionEditTruckInfo } from '../components/ActionEditTruckInfo';
import { EditRouteStatus } from '../../../../transportation/components/shipments/EditRouteStatus';
import { EditShipmentActionList } from '../../../../transportation/components/shipments/EditShipmentActionList';
import { ShipmentDocuments } from '../../../../transportation/components/shipments/ShipmentDocuments';
import { TransportationUtils } from '../../../../transportation/helpers';
import { DeliveryStatus } from '../../../../transportation/models/enums/delivery-status';
import { ShipmentActionType } from '../../../../transportation/models/enums/shipment-action-type';
import { ShipmentStatus } from '../../../../transportation/models/enums/shipment-status';
import { DeliveryShipmentAction, ShipmentAction } from '../../../../transportation/models/shipments/shipment-action';
import { TruckRead } from '../../../../transportation/models/trucks/truck-read';
import { TransportationSvc } from '../../../../transportation/service';
import '../../../../transportation/styles.scss';
import { ShipmentType } from '../../../../transportation/models/enums/shipment-type';

interface IProps {
    location: any;
    match: any;
}

export const EditTruckScene = (props : IProps) => {

    const dispatch                  = useDispatch();
    const [service]                 = React.useState(new TransportationSvc());
    const [showInfoFlyout, setShowInfoFlyout] = React.useState(false);
    const [truck, setTruck]         = React.useState({} as TruckRead); 
    const [editTruck, setEditTruck] = React.useState({} as TruckRead);
    
    async function getTruck(truckId : number) {

        if (truckId) {
            const result = await service.getTruck(truckId);
            if (result.status === StatusCode.Success) {
                setTruck(result.data);
                return;
            }
        }
        else {
            history.goBack();
        }
    }

    const refreshTruck = async () => {
        getTruck(truck.id);
    };

    /**
     * Respond to changes in the location
     */
    React.useEffect(() => {
        const opts = props.location.pathname.split(Routes.SHIPPING_TRUCK_EDIT + '/');        
        getTruck(Number.parseInt(opts[1], 10));
    }, [props.location]);

    /**
     * Move the truck's status to the next state.
     * Open -> In Transit -> Closed
     */
    const updateTruckState = async () => {
        const result = await service.updateTruckStatus(truck);
        if (result.status === StatusCode.Success) {
            setTruck(result.data);
        }
    };
  
    /**
     * Mark the provided stop as delivered and update the truck state
     * @param stop The delivery to mark as delivered
     */
    const markStopDelivered = async (stop : ShipmentAction) => {
        const result = await service.markStopDelivered(stop, truck.id, truck.rowVersion, ShipmentType.Truck);
        if (result.status === StatusCode.Success) {
            const t = {...truck} as TruckRead;
            t.rowVersion = result.data;
            t.actions
                .filter((s : ShipmentAction) => s.type === ShipmentActionType.Delivery )
                .forEach((cd: DeliveryShipmentAction) => {
                    if (cd.id === stop.id) {
                        cd.status = DeliveryStatus.Completed;
                    }
                });

            setTruck(t);
        }
    };

    const deleteStop = async (stopId : number) => {

        const result = await service.deleteStopFromRoute(
            stopId,
            truck.id,
            truck.rowVersion,
            ShipmentType.Truck
        );

        if (result.status === StatusCode.Success) {
            await refreshTruck();
        }

        return result.status;
    };
    
    /**
     * Handler for deleting the current shipment
     */
    const onDelete = async () => {
        if (truck.id && !TransportationUtils.hasDeliveredStop(truck)) {

            const result = await ResultDialog.show({
                title: 'Delete?',
                message: 'Are you sure you want to delete this truck?'
            });

            if (result) {                
                await service.deleteRoute(truck.id, truck.rowVersion, truck.type);
            }            
        }
    };

    const showEditBasicInfo = () => {
        const t = cloneDeep(truck);
        setEditTruck(t);
        setShowInfoFlyout(true);
    };

    const hideEditBasicInfo = async () => {
        setEditTruck({} as unknown as TruckRead);
        setShowInfoFlyout(false);
    };

    const onSetBasicInfoSuccess = () => {
        hideEditBasicInfo();
        refreshTruck();
    };

    return !AppUtils.IsEmpty(truck) 
        ? <PageContent classes="edit-route" pageName={PageNames.TRANSPORTATION_TRUCK_DETAILS}>
            <PageHeading 
                shouldStick={true} 
                showBackBtn={ true }
                onBackClick={ () => navigateToPage(Routes.SHIPPING_TRUCKS) }
                title={ `${ truck.name } [${ truck.id }]` }>
                {
                    (truck.status !== ShipmentStatus.Cancelled && truck.status !== ShipmentStatus.Closed)
                        ? <PageActions
                                id="pickup-edit-form"
                                onEdit={ showEditBasicInfo }
                                editPermissions={[
                                    Permissions.ShippingTrucksEdit
                                ]} 
                                onDelete={ onDelete }
                                onDeleteDisable={ TransportationUtils.hasDeliveredStop(truck) }
                                deletePermissions={[
                                    Permissions.ShippingTrucksDelete
                                ]} 
                                />
                        : null
                }
            </PageHeading>
            <PageBody>
                <div className="row">
                    <div className="col-9">

                        <DataGroupHeader title="Basic Info" />
                        
                        <div className="row">
                            <div className="col-6">
                                <ReadView
                                    label="Name"
                                    value={ truck.name }
                                />
                            </div>
                            <div className="col-6">
                                <ReadView
                                    label="Service Center"
                                    value={ truck.serviceCenterId }
                                />
                            </div>
                        </div>

                        <div className="row">
                            <div className="col-6">
                                <ReadView
                                    label="Cutoff Date"
                                    value={ truck.cutoffDate ? toSlashedShortDate(truck.cutoffDate) : '' }
                                />
                            </div>
                            <div className="col-6">
                                <ReadView
                                    label="Driver"
                                    value={ truck.driver ? truck.driver?.fullName : '' }
                                />
                            </div>
                        </div>

                        <div className="row">
                            <div className="col-6">
                                <ReadView
                                    label="Tractor"
                                    value={ truck.tractor ? truck.tractor?.name : '' }
                                />
                            </div>
                            <div className="col-6">
                                <ReadView
                                    label="Trailer"
                                    value={ truck.trailer ? truck.trailer?.name : '' }
                                />
                            </div>
                        </div>

                        {
                            truck.note && truck.note.length > 0
                            ? <React.Fragment>
                                <DataGroupHeader title="Notes" />
                                <div className="row">
                                    <div className="col-12">
                                        <p>{ truck.note }</p>
                                    </div>
                                </div>
                            </React.Fragment>
                            : null
                        }

                        <DataGroupHeader title="Truck Summary" />
                        
                        <div className="row">
                            <div className="col-6">
                                <ReadView
                                    label="Departure Time"
                                    value={ truck.departureTime }
                                    placeholder="Not Set"
                                />
                            </div>
                            <div className="col-6">
                                <ReadView
                                    label="Return Time"
                                    value={ truck.returnTime }
                                />
                            </div>
                        </div>

                        <div className="row">
                            <div className="col-6">
                                <ReadView
                                    label="Total Distance"
                                    value={ `${ truck.totalDistance } miles` }
                                />
                            </div>
                            <div className="col-6">
                                <ReadView
                                    label="Total Travel Time"
                                    value={ truck.totalTravelTime }
                                />
                            </div>
                        </div>

                        <DataGroupHeader title="Truck Metrics" />
                        
                        <div className="row">
                            <div className="col-6">
                                <ReadView
                                    label="Units"
                                    value={ truck.metrics.units }
                                    placeholder="N/A"
                                />
                            </div>
                            <div className="col-6">
                                <ReadView
                                    label="Weight"
                                    value={ truck.metrics.weight }
                                    placeholder="N/A"
                                />
                            </div>
                        </div>

                        <div className="row">
                            <div className="col-6">
                                <ReadView
                                    label="Singles"
                                    value={ truck.metrics.singles }
                                    placeholder="N/A"
                                />
                            </div>
                            <div className="col-6">
                                <ReadView
                                    label="Sections"
                                    value={ truck.metrics.sections }
                                    placeholder="N/A"
                                />
                            </div>
                        </div>

                        <div className="row">
                            <div className="col-6">
                                <ReadView
                                    label="Track"
                                    value={ truck.metrics.track }
                                    placeholder="N/A"
                                />
                            </div>
                            <div className="col-6">
                                <ReadView
                                    label="Hardware"
                                    value={ truck.metrics.hardware }
                                    placeholder="N/A"
                                />
                            </div>
                        </div>

                        <div className="row">
                            <div className="col-6">
                                <ReadView
                                    label="Feet"
                                    value={ truck.metrics.feet }
                                    placeholder="N/A"
                                />
                            </div>
                        </div>
                    </div>
                    
                    <div className="col-3 shipment-sidebar">
                        <EditRouteStatus
                            route={ truck } 
                            requiredPermission={ Permissions.ShippingTrucksEdit }
                            updateRouteState={ updateTruckState }
                        />
                        
                        {
                            truck.status !== ShipmentStatus.Cancelled
                                ? <ShipmentDocuments route={ truck } />
                                : null
                        }
 
                    </div>
                </div>

                <div className="row divider-row">
                    <div className="col-md-12">
                        <EditShipmentActionList
                            route={ truck }
                            refreshRoute={ refreshTruck }
                            markStopDelivered={ markStopDelivered }
                            deleteStop={ deleteStop }
                        />
                    </div>
                </div>
               
                <ActionEditTruckInfo
                    visible={ showInfoFlyout }
                    onSuccess={ onSetBasicInfoSuccess }
                    onClose={ hideEditBasicInfo }
                    truck={ editTruck }                    
                />

            </PageBody>
        </PageContent>
        : null;
};
